import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Card from 'reactstrap/lib/Card';

import { createDate, getDifferenceInDays } from 'site-modules/shared/utils/date-utils';
import {
  IncentivesCreativeConfigDefaultProps,
  IncentivesCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import {
  CommonAdDefaultProps,
  CommonAdPropTypes,
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { INCENTIVES_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';

import { AdLabel } from 'client/site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { Disclaimer } from 'client/site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { addZipCode, definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import {
  getDisplayHeadline,
  getTrackingValue,
} from 'site-modules/shared/components/native-ad/utils/offer-incentives-ad-utils';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { ConquestRibbon } from 'site-modules/shared/components/native-ad/native-ad-components/conquest-ribbon/conquest-ribbon';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';

import { CPO_PRODUCT_SKU } from 'site-modules/shared/components/native-ad/native-ad-components/cpo-features/cpo-constants';

import { Styles } from './incentives-site-served-ad-styles';

const DEFAULT_HEADLINE = 'See Offers %%PATTERN:zip%%';
const EXPIRES_DAYS_STYLES = {
  false: { marginLeft: '24px' },
};

export function IncentivesSiteServedAdUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  creativeConfig = IncentivesCreativeConfigDefaultProps,
  isConquest = SiteServedAdDefaultProps.isConquest,
  customTargeting = CommonAdDefaultProps.customTargeting,
  mobile = SiteServedAdDefaultProps.mobile,
  position = SiteServedAdDefaultProps.position,
  cardWrapper = null,
  horizontalLayout = false,
  plainLayout = false,
  dropShadowLayout = false,
  showDropShadow = false,
  ads11455Ads11617Recipe = 'ctrl',
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    ipZipCode,
    offersNumber,
    endDate,
    location,
    creativeConfigData: {
      logo,
      linkDisclaimer,
      disclaimerCopy,
      headline,
      headline2,
      linkDisplayUrl,
      cta,
      lineItemId,
      siteServedCreativeId,
      target,
      productSku,
    },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      const trackingValue = getTrackingValue(headline, offersNumber, zipCode, ipZipCode, DEFAULT_HEADLINE, 15, 15);
      adLoadTrackingListener(event, trackingValue);
    },
    [adLoadTrackingListener, headline, ipZipCode, offersNumber, zipCode]
  );

  if (!logo || !cta) {
    return null;
  }

  const displayHeadline = getDisplayHeadline(
    headline,
    offersNumber,
    zipCode,
    ipZipCode,
    DEFAULT_HEADLINE,
    15,
    15,
    location
  );

  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';
  const nativeStyle = INCENTIVES_AD.NATIVE_STYLE;

  const linkDisplay = (
    <div className="text-blue-40 text-center" style={{ ...Styles.mt_0_25, ...Styles.disclaimer }}>
      {linkDisplayUrl}
    </div>
  );

  const isCpoFeaturesEnabled = productSku?.includes(CPO_PRODUCT_SKU);
  const usePlainLayout = plainLayout || (isCpoFeaturesEnabled && mobile);

  const conquestRibbonStyle = { ...Styles.conquestRibbon, marginLeft: usePlainLayout ? '-16px' : 0 };

  const cardStyle = {
    ...(usePlainLayout ? { border: 'none' } : { overflow: 'hidden' }),
    ...(dropShadowLayout ? Styles.dropShadowLayout : {}),
    ...(showDropShadow ? Styles.showDropShadow : {}),
  };
  const containerStyles = dropShadowLayout ? Styles.px_1_25 : { ...(usePlainLayout ? {} : Styles.px_1_5) };
  const headlineStyles = dropShadowLayout ? Styles.headline_wired : Styles.headline;
  const ctaContainerStyles = {
    ...Styles.alignSelfStart,
    ...containerStyles,
    ...(horizontalLayout ? {} : Styles.mt_0_75),
    ...(linkDisplayUrl ? Styles.mb_0_5 : Styles.withoutLinkDisplayUrl[mobile]),
  };
  const ctaStyles = dropShadowLayout
    ? { ...Styles.fontWeight500, ...Styles.py_0_75 }
    : { ...Styles.fontWeightNormal, ...Styles.py_0_75 };

  const isAds11455Ads11617Enabled = ['chal1', 'chal2'].includes(ads11455Ads11617Recipe) && !!offersNumber && !!endDate;

  const offerExpirationContainer = isAds11455Ads11617Enabled && (
    <span
      className="bg-cool-gray-100 fw-medium size-12 text-cool-gray-30 px-0_75 py-0_25"
      style={{
        borderRadius: '24px',
        border: '1px solid #cbd4e1',
        ...EXPIRES_DAYS_STYLES[mobile],
      }}
    >
      Offer expires in <span className="text-red-40">{getDifferenceInDays(createDate(endDate))} days</span>
    </span>
  );

  return (
    <Card
      className={classnames('incentives-site-served pos-r w-100', cardWrapper, {
        'px-1': isCpoFeaturesEnabled && mobile,
      })}
      data-tracking-parent={nativeStyle}
      data-viewability={`${nativeStyle}-${position}`}
      innerRef={adRef}
      style={cardStyle}
    >
      <DfpTracking
        lineItemId={lineItemId}
        siteServedCreativeId={siteServedCreativeId}
        mobile={mobile}
        nativeStyle={nativeStyle}
        position={position}
        customTargeting={customTargeting}
        slotRenderEndListener={loadTrackingListener}
        siteServedAdsUpdater={siteServedAdsUpdater}
      />
      {adTrackers}
      <a
        className="site-served-ad-link no-focus text-decoration-none d-block"
        href={ctaURL}
        target={ctaTarget}
        rel="noopener noreferrer"
        onClick={adClickTrackingListener}
      >
        {!usePlainLayout && <AdLabel styles={dropShadowLayout ? Styles.dropShadowAdLabel[mobile] : null} />}
        <div
          className={classnames('w-100', {
            'd-flex justify-content-between align-items-center': plainLayout,
          })}
          style={dropShadowLayout ? Styles.dropShadowOemLogo : Styles.my_0_25}
        >
          {isConquest ? (
            <Fragment>
              <ConquestRibbon style={conquestRibbonStyle} />
              {ads11455Ads11617Recipe === 'chal1' && offerExpirationContainer}
            </Fragment>
          ) : (
            <Fragment>
              {(!isAds11455Ads11617Enabled || ads11455Ads11617Recipe !== 'chal1') && (
                <img
                  className={classnames({
                    'ms-0_5': !usePlainLayout,
                    'ms-1_25': dropShadowLayout,
                  })}
                  style={Styles.oemLogo}
                  alt=""
                  src={logo}
                  loading="lazy"
                />
              )}
              {ads11455Ads11617Recipe === 'chal1' && offerExpirationContainer}
            </Fragment>
          )}
          {usePlainLayout && <div style={{ fontSize: '10px', textAlign: 'right' }}>Sponsored</div>}
        </div>
        <div className={classnames('d-flex justify-content-between', { 'flex-column': !horizontalLayout })}>
          <div style={containerStyles}>
            <div
              className="headline fw-bold text-cool-gray-10"
              style={{
                ...Styles.mb_0_25,
                ...(mobile ? Styles.headline_mobile : headlineStyles),
              }}
            >
              {(!isAds11455Ads11617Enabled || ads11455Ads11617Recipe !== 'chal2') && (
                <ContentFragment componentToUse="span">{displayHeadline}</ContentFragment>
              )}
              {isAds11455Ads11617Enabled && ads11455Ads11617Recipe === 'chal2' && (
                <Fragment>
                  {offersNumber} Offers expiring <span className="text-red-40">{endDate}</span>
                </Fragment>
              )}
            </div>
            <div
              className="small text-gray-darker mb-0_5"
              style={dropShadowLayout ? Styles.dropShadowHeadline2 : Styles.headline2}
            >
              <ContentFragment>{headline2}</ContentFragment>
            </div>
          </div>
          <div className={classnames({ 'w-100': usePlainLayout })} style={ctaContainerStyles}>
            <div
              className={classnames('btn text-nowrap text-transform-none size-16', {
                'py-0_75 px-1': dropShadowLayout,
                'py-0_5 px-1': !dropShadowLayout,
                'btn-blue-50': !usePlainLayout && !isConquest,
                'btn-outline-blue-40': !usePlainLayout && isConquest,
                'btn-outline-blue-50 w-100': usePlainLayout,
              })}
              style={ctaStyles}
            >
              <CtaButtonText text={cta} />
            </div>
            {linkDisplay}
          </div>
        </div>
      </a>
      {disclaimerCopy && (
        <Disclaimer
          linkDisclaimer={linkDisclaimer}
          disclaimerText={disclaimerCopy}
          buttonStyle={mobile ? { lineHeight: '1.5rem' } : null}
        />
      )}
    </Card>
  );
}

IncentivesSiteServedAdUI.propTypes = {
  ...SiteServedAdPropTypes,
  customTargeting: CommonAdPropTypes.customTargeting,
  creativeConfig: IncentivesCreativeConfigPropTypes,
  cardWrapper: PropTypes.string,
  horizontalLayout: PropTypes.bool,
  plainLayout: PropTypes.bool,
  dropShadowLayout: PropTypes.bool,
  showDropShadow: PropTypes.bool,
  ads11455Ads11617Recipe: PropTypes.string,
};

export const IncentivesSiteServedAd = siteServedAdTrackingWrapper(IncentivesSiteServedAdUI);
